/**
 * REQUIRED_INDIVIDUAL_FIELDS — THE single source of truth for individual
 * person completeness (Bill 2026-07-23, Peter's-cart post-mortem).
 *
 * Consumers (keep in lockstep — that's the point):
 *  1. WorkflowEngine required-field gates (services/rules/IndividualGates.ts
 *     + JointIndividualGates.ts) — what blocks the Review screen.
 *  2. The person Add/Edit modal save gate (IndividualReviewBody) — what the
 *     FSR is stopped from saving without.
 *  3. GateQuickFixModal — the in-place fix-it micro-modal on Review.
 *  4. (documented parity) server commit-validate per-person CustAdd
 *     completeness: CartCommitService.ValidateAsync requires dob/addr/city/
 *     state/zip (+ name/taxId via CustAdd itself) and SKIPS people with a
 *     cifId — mirror that skip everywhere client-side.
 *
 * The DOB trap this kills: the modal had NO completeness check, the engine
 * blocked on MISSING_DOB, the banner never named it, and the chips (a third
 * rule set) stayed green. One list, consumed everywhere, ends the divergence.
 */

export interface RequiredIndividualField {
  /** PersonSnapshot / person-draft key (identical by design). */
  field: 'firstName' | 'lastName' | 'taxId' | 'dob' | 'addr' | 'city' | 'state' | 'zip';
  label: string;
}

export const REQUIRED_INDIVIDUAL_FIELDS: ReadonlyArray<RequiredIndividualField> = [
  { field: 'firstName', label: 'First name' },
  { field: 'lastName', label: 'Last name' },
  { field: 'taxId', label: 'SSN' },
  { field: 'dob', label: 'Date of birth' },
  { field: 'addr', label: 'Street address' },
  { field: 'city', label: 'City' },
  { field: 'state', label: 'State' },
  { field: 'zip', label: 'ZIP code' },
];

/**
 * No-TIN proof (Bill 2026-10-14). A person with no SSN/TIN/ATIN (TIN code N) or a non-resident
 * alien with no US TIN (Forn) gets a bank-assigned CIF at commit — but only once Core has been
 * searched by name + date of birth for THIS identity and nothing was found (or the officer
 * dismissed the match): the customer may already be on file. The early duplicate check stamps
 * `noTinClearedKey` with the identity it searched; editing the name or DOB changes the key and
 * silently revokes the waiver. Same pattern as the beneficiary editor's noSsnClearedKey.
 */
export function noTinIdentityKey(p: { firstName?: unknown; lastName?: unknown; dob?: unknown; name2?: unknown }): string {
  const s = (v: unknown): string => (typeof v === 'string' ? v.trim().toLowerCase() : '');
  const dob = s(p.dob).replace(/\D/g, '');
  if (s(p.firstName) && s(p.lastName) && dob.length >= 8) return `nd:${s(p.firstName)}|${s(p.lastName)}|${dob}`;
  // A business or trust has no date of birth: its proof is a Core search by the entity name
  // (Bill 2026-10-14, "No EIN / TIN"). Only when there is no person name at all.
  if (!s(p.firstName) && !s(p.lastName) && s(p.name2)) return `ne:${s(p.name2).replace(/\s+/g, ' ')}`;
  return '';
}

/** True when the SSN requirement is waived: TIN code N/Forn AND a no-TIN proof for THIS identity. */
export function noTinWaivesTaxId(p: {
  tinCode?: unknown; noTinClearedKey?: unknown; firstName?: unknown; lastName?: unknown; dob?: unknown; name2?: unknown;
}): boolean {
  if (p.tinCode !== 'N' && p.tinCode !== 'Forn') return false;
  const key = noTinIdentityKey(p);
  return key.length > 0 && p.noTinClearedKey === key;
}

/** Missing-field LABELS for any person-shaped object (draft or snapshot).
 * Callers apply the existing-customer skip (`!person.cifId`) themselves —
 * mirroring the server's commit-validate skip for people Core already has. */
export function missingRequiredIndividualFields(
  person: Partial<Record<RequiredIndividualField['field'] | 'tinCode' | 'noTinClearedKey', unknown>>,
): string[] {
  const missing: string[] = [];
  const waiveTaxId = noTinWaivesTaxId(person);
  for (const spec of REQUIRED_INDIVIDUAL_FIELDS) {
    if (spec.field === 'taxId' && waiveTaxId) continue;
    const v = person[spec.field];
    if (typeof v !== 'string' || v.trim().length === 0) missing.push(spec.label);
  }
  return missing;
}
